import classNames from 'classnames'
import { omit } from 'lodash'
import { Children, forwardRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ButtonBase,
  ButtonIcon,
  ButtonLabel,
  DISABLED_HANDLER_OVERRIDES,
  resolveDisabledState,
  resolveFluentContent,
} from '../ButtonLike'
import * as styles from './styles'
import {
  type Button2Props,
  type ButtonLinkProps,
  type DeclarativeResolverProps,
  type SimpleButtonLinkProps,
  type SimpleButtonProps,
} from './types'

export { selectors as button2Selectors } from './styles'

/**
 * Shared prop handling for the declarative `<Button2>`/`<ButtonLink>`:
 * - Resolve the disabled state
 * - Validate that there's at least one child
 * - Apply the styling hooks (`className`, `data-size`, `data-variant`)
 */
function resolveDeclarativeProps<P extends DeclarativeResolverProps>(
  props: P,
  componentName: string
) {
  const {
    className,
    'aria-disabled': ariaDisabled,
    disabled,
    size = 'regular',
    variant = 'primary',
    ...remainingProps
  } = props

  if (Children.count(props.children) < 1) {
    throw new TypeError(`${componentName}: must provide at least one child`)
  }

  const isDisabled = resolveDisabledState(disabled, ariaDisabled)

  return [
    {
      ...remainingProps,
      // The native `disabled` attribute blocks all interaction with the `<button>`, so users can't reach it to find out
      // _why_ it's disabled. `aria-disabled` has the desired semantics.
      'aria-disabled': isDisabled,
      className: classNames(styles.selectors.root, className),
      'data-size': size,
      'data-variant': variant,
    },
    isDisabled,
  ] as const
}

/**
 * An HTML `<button>` with Gem styling, taking arbitrary JSX children. {@link SimpleButton} may meet your needs.
 *
 * If you need to customize the styling beyond overriding the CSS variables for color, you should probably use
 * `<ButtonBase>` or a plain HTML `<button>` instead.
 *
 * @example
 * <Button2>
 *   <Button2.Icon glyph="gem-download" />
 *   <Button2.Label>Download</Button2.Label>
 * </Button2>
 * <Button2 size="small" variant="secondary">
 *   <Button2.Label visuallyHidden={true}>Download</Button2.Label>
 *   <Button2.Icon glyph="gem-download" />
 * </Button2>
 * <Button2 disabled>
 *   <StatusIndicator status={Status.Invalid} />
 *   <Button2.Label>Save</Button2.Label>
 * </Button2>
 *
 * @note This MUST NOT be used be used for routing-based navigation. Use `<ButtonLink>` (or `<SimpleButtonLink>`)
 * instead.
 * @note Differences from a raw `<button>`:
 * - `type` defaults to `button` instead of `submit`
 */
export const Button2 = Object.assign(
  forwardRef<HTMLButtonElement, Button2Props>(function Button2(props, ref) {
    const [resolvedProps] = resolveDeclarativeProps(props, 'Button2')

    return <ButtonBase css={styles.root} ref={ref} {...resolvedProps} />
  }),
  { Icon: ButtonIcon, Label: ButtonLabel }
)
Button2.displayName = 'Lapidary: Button2'

/**
 * A `react-router` `<Link>` styled just like {@link Button2}. Use `<Button2>` instead unless you need
 * link-specific features — for example, route navigation, opening something in a new window (`target`), or
 * triggering a download (`download`). And as with `<Button2>`, use a plain `<Link>` unless you want the styling.
 */
export const ButtonLink = Object.assign(
  forwardRef<HTMLAnchorElement, ButtonLinkProps>(
    function ButtonLink(props, ref) {
      const [resolvedProps, disabled] = resolveDeclarativeProps(
        props,
        'ButtonLink'
      )

      // `<a>` and `<Link>` don't have a meaningful disabled staate, but the most-correct option is to omit the `href`
      // attribute. `<Link>` requires the `to` prop, so it's better to manually declare `<a>` and short-circuit.
      if (disabled) {
        const anchorProps = omit(resolvedProps, [
          'preventScrollReset',
          'relative',
          'reloadDocument',
          'replace',
          'state',
          'to',
          'viewTransition',
        ])

        return (
          // `anchor-has-content` doesn't know `children` is in `anchorProps`
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            css={[styles.root, styles.linkRoot]}
            ref={ref}
            role="link"
            // `<a>` without an `href` doesn't appear in the tab order
            tabIndex={0}
            {...anchorProps}
            {...DISABLED_HANDLER_OVERRIDES}
          />
        )
      }

      return (
        <Link
          css={[styles.root, styles.linkRoot]}
          ref={ref}
          {...resolvedProps}
        />
      )
    }
  ),
  { Icon: ButtonIcon, Label: ButtonLabel }
)
ButtonLink.displayName = 'Lapidary: ButtonLink'

/**
 * Convenience wrapper for {@link Button2} that makes the most common use cases trivially easy: just a label, just an
 * icon, or an icon followed by a label. Takes a string as its child, and/or a `glyph`.
 *
 * @example
 * <SimpleButton>Hello</SimpleButton>
 * <SimpleButton glyph="gem-add">Create</SimpleButton>
 * <SimpleButton aria-label="Settings" glyph="gem-settings" />
 */
export const SimpleButton = forwardRef<HTMLButtonElement, SimpleButtonProps>(
  function SimpleButton({ children, glyph, ...remainingProps }, ref) {
    return (
      <Button2 {...remainingProps} ref={ref}>
        {resolveFluentContent(
          { children, glyph, ...remainingProps },
          'SimpleButton'
        )}
      </Button2>
    )
  }
)
SimpleButton.displayName = 'Lapidary: SimpleButton'

/**
 * Convenience wrapper for {@link ButtonLink} with the same fluent API as {@link SimpleButton}. Use this instead of
 * `<SimpleButton>` when you need link-specific features (see {@link ButtonLink}).
 */
export const SimpleButtonLink = forwardRef<
  HTMLAnchorElement,
  SimpleButtonLinkProps
>(function SimpleButtonLink({ children, glyph, ...remainingProps }, ref) {
  return (
    <ButtonLink {...remainingProps} ref={ref}>
      {resolveFluentContent(
        { children, glyph, ...remainingProps },
        'SimpleButtonLink'
      )}
    </ButtonLink>
  )
})
SimpleButtonLink.displayName = 'Lapidary: SimpleButtonLink'
